import {
  AspectRatio,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@izicvs/ui";
import { cn, isPremiumTemplate, templatesList } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { motion } from "framer-motion";
import { Check, Crown } from "lucide-react";
import { useState } from "react";

import { useSubscription, useTemplatesPass } from "@/client/services/billing";
import { TEMPLATE_DEFAULTS } from "@/client/services/resume/templates";
import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../shared/section-icon";

// Purchase popup for the weekly premium-templates pass. Shown when a user who is
// not entitled clicks a Pro template, instead of blurring it.
const PremiumTemplateDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { subscribe, loading } = useTemplatesPass();

  const perks = [
    t`Every premium template`,
    t`Export and share without limits`,
    t`Cancel anytime, in one click`,
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Crown className="size-6 text-primary" />
            {t`Unlock premium templates`}
          </DialogTitle>
          <DialogDescription>
            {t`Use every premium design on your resume, export and share it freely.`}
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 flex items-baseline gap-1.5">
          <span className="text-4xl font-black tracking-tight">2,99 €</span>
          <span className="text-sm opacity-60">{t`/ week`}</span>
        </div>

        <ul className="space-y-2.5 text-sm">
          {perks.map((perk) => (
            <li key={perk} className="flex items-center gap-2.5">
              <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/15">
                <Check className="size-3 text-primary" strokeWidth={3} />
              </span>
              {perk}
            </li>
          ))}
        </ul>

        <Button
          className="mt-4 w-full"
          size="lg"
          disabled={loading}
          onClick={() => void subscribe()}
        >
          {t`Unlock for 2,99 € / week`}
        </Button>
        <p className="text-center text-xs opacity-50">
          {t`Billed weekly. Pro and Autopilot plans already include every template.`}
        </p>
      </DialogContent>
    </Dialog>
  );
};

// Signature theme/font defaults live in @/client/services/resume/templates
// (shared with the creation onboarding). Premium templates are gated by
// @izicvs/utils#isPremiumTemplate (shared with the server too).

export const TemplateSection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const currentTemplate = useResumeStore((state) => state.resume.data.metadata.template);

  const { subscription } = useSubscription();
  const [passOpen, setPassOpen] = useState(false);

  const billingEnabled = subscription?.billingEnabled ?? false;
  // Entitled to premium templates via a Pro-level plan or the weekly pass.
  const canUseTemplates = subscription?.templatesAccess ?? false;

  const selectTemplate = (template: string) => {
    setValue("metadata.template", template);

    const defaults = TEMPLATE_DEFAULTS[template];
    if (!defaults) return;

    setValue("metadata.theme.primary", defaults.primary);
    if (defaults.text) setValue("metadata.theme.text", defaults.text);
    if (defaults.background) setValue("metadata.theme.background", defaults.background);
    if (defaults.font) {
      setValue("metadata.typography.font.family", defaults.font.family);
      setValue("metadata.typography.font.subset", "latin");
      setValue("metadata.typography.font.variants", defaults.font.variants);
    }
  };

  return (
    <section id="template" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="template" size={18} name={t`Template`} />
          <h2 className="line-clamp-1 text-2xl font-bold lg:text-3xl">{t`Template`}</h2>
        </div>
      </header>

      <main className="grid grid-cols-2 gap-8 @lg/right:grid-cols-3 @2xl/right:grid-cols-4">
        {templatesList.map((template, index) => {
          const isPremium = isPremiumTemplate(template);
          // Premium templates stay fully visible; clicking one without access
          // opens the purchase popup instead of applying it.
          const gated = isPremium && billingEnabled && !canUseTemplates;

          return (
            <AspectRatio key={template} ratio={1 / 1.4142}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: index * 0.1 } }}
                whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
                className={cn(
                  "relative cursor-pointer overflow-hidden rounded-sm bg-secondary ring-primary transition-all hover:ring-2",
                  currentTemplate === template && "ring-2",
                )}
                onClick={() => {
                  if (gated) {
                    setPassOpen(true);
                    return;
                  }
                  selectTemplate(template);
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold capitalize text-foreground/40">
                  {template}
                </div>
                <img
                  src={`/templates/jpg/${template}.jpg`}
                  alt={template}
                  className="relative rounded-sm"
                  onError={(event) => {
                    event.currentTarget.style.visibility = "hidden";
                  }}
                />

                {isPremium && (
                  <span className="absolute right-1.5 top-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground shadow">
                    <Crown className="size-2.5" />
                    {t`Pro`}
                  </span>
                )}

                <div className="absolute inset-x-0 bottom-0 h-32 w-full bg-gradient-to-b from-transparent to-background/80">
                  <p className="absolute inset-x-0 bottom-2 text-center font-bold capitalize text-primary">
                    {template}
                  </p>
                </div>
              </motion.div>
            </AspectRatio>
          );
        })}
      </main>

      <PremiumTemplateDialog open={passOpen} onOpenChange={setPassOpen} />
    </section>
  );
};
